import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { auth } from '../../firebase';
import examService from '../../services/examService';
import enhancedUserService from '../../services/enhancedUserService';

const PROVIDERS = [
  { id: 'telc', name: 'TELC' },
  { id: 'goethe', name: 'Goethe' },
  { id: 'osd', name: 'ÖSD' }
];
const LEVELS = ['B1', 'B2', 'C1'];

// Admin-only quick switch for the selected exam, shown in the header in place
// of the static "TELC B2" label. One tap on a provider/level cell switches.
const AdminExamSwitcher = ({ examProvider, examLevel }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(null);
  const rootRef = useRef(null);

  const currentProvider = String(examProvider || '').toLowerCase();
  const currentLevel = String(examLevel || '').toUpperCase();

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const handleSwitch = async (provider, level) => {
    if (switching) return;
    if (provider === currentProvider && level === currentLevel) {
      setOpen(false);
      return;
    }
    const userId = auth.currentUser?.uid;
    if (!userId) return;

    setSwitching(`${provider}-${level}`);
    try {
      const examData = await examService.loadExam(provider, level);
      localStorage.setItem(`selectedExamData_${userId}`, JSON.stringify(examData));
      sessionStorage.setItem('selectedExamData', JSON.stringify(examData));
      localStorage.setItem('selectedExamData', JSON.stringify(examData));
      await enhancedUserService.saveExamSelection(userId, examData);
      window.dispatchEvent(new CustomEvent('examSelected', { detail: examData }));

      // Exam-scoped URLs (/:lang/exams/telc-b2/...) would keep showing the old
      // exam, so land on the dashboard instead; anywhere else just reload.
      const [, lang] = window.location.pathname.split('/');
      if (window.location.pathname.includes('/exams/')) {
        window.location.assign(`/${lang}/dashboard`);
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error switching exam:', error);
      alert(t('header.adminExamSwitch.error'));
      setSwitching(null);
    }
  };

  return (
    <div ref={rootRef} className="relative mt-0.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 -ml-1.5 px-1.5 py-0.5 rounded-md text-xs font-semibold text-slate-600 hover:text-black hover:bg-slate-100 active:scale-95 transition-all duration-150 touch-manipulation"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={t('header.adminExamSwitch.label')}
      >
        {currentProvider && currentLevel
          ? `${currentProvider.toUpperCase()} ${currentLevel}`
          : t('header.adminExamSwitch.title')}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full mt-2 z-50 w-[248px] max-w-[calc(100vw-32px)] rounded-2xl bg-white border-2 border-black p-3 shadow-[4px_4px_0_0_#3A86FF] animate-scale-in"
        >
          <p className="font-space text-sm font-bold text-black">{t('header.adminExamSwitch.title')}</p>
          <p className="text-[11px] text-slate-500 mb-3">{t('header.adminExamSwitch.hint')}</p>

          <div className="grid grid-cols-[auto_repeat(3,1fr)] gap-1.5 items-center">
            {PROVIDERS.map((provider) => (
              <React.Fragment key={provider.id}>
                <span className="text-xs font-bold text-slate-700 pr-1">{provider.name}</span>
                {LEVELS.map((level) => {
                  const key = `${provider.id}-${level}`;
                  const isCurrent = provider.id === currentProvider && level === currentLevel;
                  const isSwitching = switching === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      role="menuitemradio"
                      aria-checked={isCurrent}
                      disabled={Boolean(switching)}
                      onClick={() => handleSwitch(provider.id, level)}
                      className={`h-11 rounded-xl border-2 text-sm font-bold flex items-center justify-center transition-all duration-150 active:scale-95 touch-manipulation disabled:cursor-wait ${
                        isCurrent
                          ? 'bg-black text-white border-black'
                          : 'bg-white text-black border-slate-200 hover:border-[#3A86FF] hover:text-[#3A86FF]'
                      } ${switching && !isSwitching ? 'opacity-50' : ''}`}
                    >
                      {isSwitching ? (
                        <Loader2 className="w-4 h-4 animate-spin" aria-label={t('header.adminExamSwitch.switching')} />
                      ) : level}
                    </button>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminExamSwitcher;
